/**
 * Studio-timezone conversions for dashboard datetime inputs + displays.
 *
 * `<input type="datetime-local">` has NO timezone — its value is a bare
 * wall-clock `YYYY-MM-DDTHH:mm`. The previous helpers converted it via
 * `new Date(local)` / `Date#getHours`, which interpret the wall-clock in the
 * BROWSER's timezone. An operator whose machine isn't on the studio's zone
 * (or one viewing from elsewhere) then stored/read the wrong instant — e.g.
 * entering "7pm" produced `19:00Z` (= 8pm Europe/London) instead of `18:00Z`.
 *
 * These helpers pin every conversion to the STUDIO timezone regardless of
 * the browser, using `Intl.DateTimeFormat` (full-ICU; no dep). The zone is
 * PER-TENANT: `DashboardLayout` calls {@link setStudioTimeZone} with the
 * `/api/whoami` `studio.timezone` (the `studios.timezone` column — the same
 * value chat/member-api/crons read) BEFORE any page renders, so a Berlin
 * studio's dashboard shows Berlin wall-clock. Until whoami lands (login
 * screen only — no instants shown there) the default is Europe/London.
 *
 * This module-level snapshot is deliberately NOT reactive: the zone changes
 * only via the /config editor's `set_studio_timezone` save, which forces a
 * reload so open pages re-pin.
 */

const DEFAULT_STUDIO_TIME_ZONE = "Europe/London";

let currentStudioTimeZone = DEFAULT_STUDIO_TIME_ZONE;

/**
 * Pin the session's studio timezone. Called by `DashboardLayout` when
 * `/api/whoami` resolves. A string ICU doesn't recognise is ignored (keeps
 * the previous zone) — a malformed DB value must degrade to wrong-label
 * times, never to a render crash in every date cell.
 */
export function setStudioTimeZone(tz: string): void {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    currentStudioTimeZone = tz;
  } catch {
    // Unknown zone id — keep the current (default) zone.
  }
}

/** The session's studio timezone (IANA id). */
export function studioTimeZone(): string {
  return currentStudioTimeZone;
}

/**
 * Cached `Intl.DateTimeFormat` pinned to the CURRENT studio timezone.
 * Formatter construction is expensive (~100µs) and table cells format
 * thousands of instants — the cache keys on locale+zone+options so a zone
 * change (post-reload) or a new option shape mints a fresh formatter.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

export function studioFormat(
  options: Intl.DateTimeFormatOptions,
  locale = "en-GB",
): Intl.DateTimeFormat {
  const tz = studioTimeZone();
  const key = `${locale}|${tz}|${JSON.stringify(options)}`;
  let f = formatterCache.get(key);
  if (f === undefined) {
    f = new Intl.DateTimeFormat(locale, { timeZone: tz, ...options });
    formatterCache.set(key, f);
  }
  return f;
}

function partsInZone(
  date: Date,
  tz: string,
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const map: Record<string, string> = {};
  for (const p of dtf.formatToParts(date)) map[p.type] = p.value;
  const hour = map["hour"] === "24" ? "00" : (map["hour"] ?? "00");
  return {
    year: Number(map["year"]),
    month: Number(map["month"]),
    day: Number(map["day"]),
    hour: Number(hour),
    minute: Number(map["minute"]),
    second: Number(map["second"] ?? "0"),
  };
}

/** Offset (ms) of `tz` at the given UTC instant: (wall-clock in tz) − UTC. */
function tzOffsetMs(date: Date, tz: string): number {
  const p = partsInZone(date, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - date.getTime();
}

/**
 * `<input type="datetime-local">` value (`YYYY-MM-DDTHH:mm`, a wall-clock in
 * `tz`) → UTC ISO-8601 (`…Z`). E.g. 19:00 Europe/London (BST) → `18:00:00Z`.
 */
export function zonedDatetimeInputToIso(
  local: string,
  tz: string = studioTimeZone(),
): string {
  if (local === "") return "";
  // Treat the wall-clock as if UTC (provisional), then back out the zone's
  // offset at that instant to get the true UTC instant.
  const provisional = new Date(`${local}:00Z`);
  if (Number.isNaN(provisional.getTime())) return local;
  const utcMs = provisional.getTime() - tzOffsetMs(provisional, tz);
  return new Date(utcMs).toISOString();
}

/**
 * UTC ISO-8601 → `<input type="datetime-local">` value (`YYYY-MM-DDTHH:mm`)
 * showing the wall-clock in `tz`. Inverse of {@link zonedDatetimeInputToIso}.
 */
export function isoToZonedDatetimeInput(
  iso: string,
  tz: string = studioTimeZone(),
): string {
  if (iso === "") return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = partsInZone(d, tz);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return (
    `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
  );
}

/**
 * UTC ISO-8601 → `YYYY-MM-DD` calendar-day key in `tz` — the day an instant
 * falls on in the STUDIO's calendar, regardless of the browser's zone (a
 * 23:30Z June slot is the NEXT London day). Empty string on unparseable input.
 */
export function isoToZonedDateKey(
  iso: string,
  tz: string = studioTimeZone(),
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = partsInZone(d, tz);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}
